import React, { useState } from 'react';
import { Box, Container, Grid, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/system';
import { projectbannerbg } from '../../../../../assets';

interface AboutGalleryProps {
  aboutsection: {
    des: string;
    images: {
      img: string;
      h1?: string;
    }[];
    highlights: {
      h1: string;
      d1: string;
    }[];
  } | null;
}

const CDN_URL =
  process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';
const resolveImageUrl = (path: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const Image = styled('img')({});

const AboutSectionGallery: React.FC<AboutGalleryProps> = ({ aboutsection }) => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [hovered, setHovered] = useState<number | null>(null);

  const isValid =
    aboutsection &&
    aboutsection.des &&
    aboutsection.images.length &&
    aboutsection.highlights.length > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 'auto', md: '100vh' },
        backgroundImage: `url(${projectbannerbg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'bottom center',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 6,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ color: 'white' }}>
          {!aboutsection ? (
            <Box textAlign="center">
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, mb: 2, color: 'white' }}
              >
                ABOUT
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontSize: '18px',
                  fontWeight: 300,
                  letterSpacing: '0.8px',
                  color: 'white',
                }}
              >
                Data is not uploaded.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: 'white',
                  // textAlign: 'start',
                  fontFamily: 'Futura,  sans-serif !important',
            letterSpacing:"2px"
                }}
              >
                ABOUT
              </Typography>

               <Box
              sx={{
                width: {
                  xs: "120px",
                  md: "180px",
                },

                height: "2px",

                background:
                  "linear-gradient(to right, transparent, #fff, transparent)",

                mx: "auto",

                mb: 3,
              }}
            />

              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '16px', md: '18px' },
                  textAlign: 'justify',
                  fontWeight: 300,
                  letterSpacing: '0.8px',
                  color: 'white',
                }}
              >
                {aboutsection.des}
              </Typography>

              {/* Image Gallery */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: 300,
                  gap: '10px',
                  overflow: 'hidden',
                  position: 'relative',
                  zIndex: 2,
                  mt: 4,
                }}
                onMouseLeave={() => setHovered(null)}
              >
                {aboutsection.images.map((imgData, index) => (
                  <Image
                    key={index}
                    src={resolveImageUrl(imgData.img)}
                    alt={imgData.h1 || `Gallery ${index + 1}`}
                    onMouseEnter={() => setHovered(index)}
                    sx={{
                      width:
                        hovered === index ||
                        (hovered === null && index === 0)
                          ? '50%'
                          : '10%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '10px',
                      border: '2px solid #fff',
                      transition: 'all ease-out 0.5s',
                      cursor: 'pointer',
                      zIndex: hovered === index ? 2 : 1,
                    }}
                  />
                ))}
              </Box>

              {/* Highlights Section */}
              <Grid
                container
                sx={{
                  mt: 4,
                  justifyContent: 'center',
                  alignItems: 'stretch',
                  textAlign: 'center',
                  position: 'relative',
                }}
              >
                {aboutsection.highlights.map((item, index) => (
                  <Grid
                    item
                    xs={6}
                    md={3}
                    key={index}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'relative',
                      px: { xs: 1, md: 2 },
                      py: { xs: 3, md: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        color: 'white',
                        height: '100%',
                      }}
                    >
                      {/* Force consistent row alignment */}
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '22px', md: '30px' },
                          lineHeight: 1.2,
                          minHeight: { xs: 35, md: 45 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        {item.h1}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 300,
                          letterSpacing: 0.5,
                          lineHeight: 1.6,
                          minHeight: { xs: 40, md: 45 },
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          color: 'white',
                        }}
                      >
                        {item.d1}
                      </Typography>
                    </Box>

                    {/* Keep your existing dividers as-is */}
                    {index !== aboutsection.highlights.length - 1 &&
                      !(isMobile && index === 1) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '-2px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '40px',
                            width: '1px',
                            background: 'white',
                            opacity: 0.5,
                          }}
                        />
                      )}

                    {isMobile &&
                      index % 2 !== 0 &&
                      index !== aboutsection.highlights.length - 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: '0%',
                            transform: 'translateX(-25%)',
                            height: '1px',
                            width: '500px',
                            background: 'white',
                            opacity: 0.5,
                          }}
                        />
                      )}
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default AboutSectionGallery;
